import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { RefreshCw, Send, Download, ExternalLink, Mail, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleString(); } catch { return String(d); }
}

export default function AdminAssessments() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ results: [], count: 0, paid_orders: 0, completed_assessments: 0 });
  const [busyToken, setBusyToken] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND}/api/admin/assessments`, { credentials: 'include' });
      if (!r.ok) {
        toast.error(`Failed to load (${r.status})`);
        setLoading(false);
        return;
      }
      const d = await r.json();
      setData(d);
    } catch {
      toast.error('Network error.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const reexport = async (token) => {
    setBusyToken(token);
    try {
      const r = await fetch(`${BACKEND}/api/admin/assessments/${encodeURIComponent(token)}/export`, {
        method: 'POST',
        credentials: 'include',
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.ok) toast.success(`Re-exported to Zapier (${d.status_code})`);
      else toast.error(`Zapier failed (${d.status_code || r.status})`);
      await load();
    } finally {
      setBusyToken(null);
    }
  };

  const resendEmail = async (token) => {
    setBusyToken(token);
    try {
      const r = await fetch(`${BACKEND}/api/admin/assessments/${encodeURIComponent(token)}/resend-email`, {
        method: 'POST',
        credentials: 'include',
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.ok) toast.success('IDP email re-sent.');
      else toast.error('Could not resend email.');
    } finally {
      setBusyToken(null);
    }
  };

  const bulkExport = async () => {
    if (!window.confirm('Re-export ALL assessments to Zapier? This will fire one POST per row.')) return;
    setBulkBusy(true);
    try {
      const r = await fetch(`${BACKEND}/api/admin/assessments/export`, {
        method: 'POST',
        credentials: 'include',
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok) toast.success(`Bulk export — sent: ${d.sent}, failed: ${d.failed}`);
      else toast.error('Bulk export failed.');
      await load();
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-assessments">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-mono-accent uppercase tracking-[0.2em] text-[10px] text-foreground/60 font-semibold">Premium · $27 IDP</div>
          <h2 className="font-heading text-xl sm:text-2xl font-bold tracking-tight">Athlete Self-Assessment</h2>
          <p className="text-sm text-foreground/60 mt-1">Paid IDP submissions. Auto-fires to Zapier on submit; re-export manually below.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="admin-assessments-refresh">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button size="sm" onClick={bulkExport} disabled={bulkBusy || (data.results || []).length === 0} className="bg-[#004C54] hover:bg-[#003a40]" data-testid="admin-assessments-bulk-export">
            {bulkBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />} Bulk export to Zapier
          </Button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid sm:grid-cols-3 gap-3">
        <Card className="border-border"><CardContent className="p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] font-mono font-bold text-foreground/55">Paid orders</div>
          <div className="font-heading text-3xl font-bold tracking-tight mt-1 tabular-nums" data-testid="admin-stat-paid">{data.paid_orders}</div>
        </CardContent></Card>
        <Card className="border-border"><CardContent className="p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] font-mono font-bold text-foreground/55">Completed assessments</div>
          <div className="font-heading text-3xl font-bold tracking-tight mt-1 tabular-nums" data-testid="admin-stat-completed">{data.completed_assessments}</div>
        </CardContent></Card>
        <Card className="border-border"><CardContent className="p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] font-mono font-bold text-foreground/55">Showing</div>
          <div className="font-heading text-3xl font-bold tracking-tight mt-1 tabular-nums" data-testid="admin-stat-shown">{data.count}</div>
        </CardContent></Card>
      </div>

      <Card className="border-border">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={`sk-${i}`} className="h-12 w-full" />)}
            </div>
          ) : (data.results || []).length === 0 ? (
            <div className="p-10 text-center text-sm text-foreground/60" data-testid="admin-assessments-empty">
              No assessments submitted yet. They&rsquo;ll appear here the moment someone completes one.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-[0.15em] font-mono font-bold text-foreground/55 border-b border-border">
                    <th className="px-5 py-3">When</th>
                    <th className="px-5 py-3">Athlete</th>
                    <th className="px-5 py-3">Overall</th>
                    <th className="px-5 py-3">Top Priority</th>
                    <th className="px-5 py-3">Code</th>
                    <th className="px-5 py-3">PDF</th>
                    <th className="px-5 py-3">Zapier</th>
                    <th className="px-5 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.results || []).map((r) => {
                    const zap = r.zapier_export || {};
                    const zapColor = zap.status === 'sent' ? 'border-emerald-300 text-emerald-700 bg-emerald-50'
                      : zap.status === 'failed' ? 'border-red-300 text-red-700 bg-red-50'
                      : 'border-amber-300 text-amber-700 bg-amber-50';
                    return (
                      <tr key={r.result_token} className="border-b border-border last:border-0" data-testid={`admin-row-${r.result_token}`}>
                        <td className="px-5 py-3 align-top whitespace-nowrap text-foreground/65 text-xs">{fmtDate(r.created_at)}</td>
                        <td className="px-5 py-3 align-top">
                          <div className="font-semibold">{r.name || '—'}</div>
                          <div className="text-xs text-foreground/55">{r.email || '—'}</div>
                        </td>
                        <td className="px-5 py-3 align-top">
                          <div className="font-mono font-bold tabular-nums">{Math.round(r.overall_score || 0)}</div>
                          <div className="text-[11px] text-foreground/55">{r.band || ''}</div>
                        </td>
                        <td className="px-5 py-3 align-top">
                          <div className="font-semibold capitalize">{r.top_priority || '—'}</div>
                          <div className="text-[11px] text-foreground/55">{r.primary_framework || ''}</div>
                        </td>
                        <td className="px-5 py-3 align-top">
                          <code className="text-xs font-mono bg-[#F0F4F4] px-2 py-1 rounded">{r.discount_code}</code>
                        </td>
                        <td className="px-5 py-3 align-top">
                          {r.pdf_sent ? (
                            <Badge variant="outline" className="border-emerald-300 text-emerald-700 bg-emerald-50">Sent</Badge>
                          ) : (
                            <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50">Pending</Badge>
                          )}
                        </td>
                        <td className="px-5 py-3 align-top">
                          <Badge variant="outline" className={zapColor}>{zap.status || 'pending'}</Badge>
                          {zap.last_response_code ? (
                            <div className="text-[11px] text-foreground/55 mt-1">HTTP {zap.last_response_code}</div>
                          ) : null}
                        </td>
                        <td className="px-5 py-3 align-top text-right whitespace-nowrap">
                          <div className="flex justify-end gap-1.5">
                            <Button asChild size="sm" variant="ghost" className="h-8" data-testid={`admin-view-${r.result_token}`}>
                              <a href={`/assessment/results/${r.result_token}`} target="_blank" rel="noreferrer" title="Open IDP">
                                <ExternalLink className="h-3.5 w-3.5" />
                              </a>
                            </Button>
                            <Button asChild size="sm" variant="ghost" className="h-8" data-testid={`admin-pdf-${r.result_token}`}>
                              <a href={`${BACKEND}/api/assessment/pdf/${r.result_token}`} target="_blank" rel="noreferrer" title="Download PDF">
                                <Download className="h-3.5 w-3.5" />
                              </a>
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8"
                              disabled={busyToken === r.result_token}
                              onClick={() => resendEmail(r.result_token)}
                              data-testid={`admin-resend-email-${r.result_token}`}
                              title="Resend IDP email"
                            >
                              <Mail className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8"
                              disabled={busyToken === r.result_token}
                              onClick={() => reexport(r.result_token)}
                              data-testid={`admin-zapier-export-${r.result_token}`}
                              title="Re-export to Zapier"
                            >
                              {busyToken === r.result_token ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Send className="h-3.5 w-3.5 mr-1" /> Zap</>}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
